import FilterDefinitionsVue from '@Types/search/FilterDefinitionsVue';
import SearchMetaDefinitionsVue from '@Types/search/SearchMetaDefinitionsVue';
import SearchResultBaseVue from "@Types/search/SearchResultBaseVue";
import { FilterSelectionLocation, FilterSelectionLocationEntry } from '../Types/FilterSelections';
import GeoBoundingBoxTSVM from '../Types/search/GeoBoundingBoxTSVM';
import GeoPointTSVM from '../Types/search/GeoPointTSVM';



declare const filterDefinitions: FilterDefinitionsVue | undefined;
declare const searchMetaDefinitions: SearchMetaDefinitionsVue | undefined;
declare const initialSearchResults: SearchResultBaseVue | undefined;


/**
 * Returns the default filter definitions as set server side, this
 * is required for each search related page that needs the specific
 * id's etc for the search to work. e.g. amenitiyIds for specific
 * filter types.
 * 
 * Note that setting these properties in c# is done through the SearchSetupHelper class.
 * 
 * @returns
 */
export function getFilterDefinitions(): FilterDefinitionsVue {

    if (!filterDefinitions) {

        throw new Error("filterDefinitions is undefined");
    }

    return filterDefinitions;
}

/**
 * Returns the search meta definitions as set server side, contains
 * specifics about the current search 'page' such as if it is from a 
 * specific landing page, contains data about the initial location selections,
 * the search url it started from, fetchUrl etc.
 * 
 * Note that setting these properties in c# is done through the SearchSetupHelper class.
 * 
 * @returns
 */
export function getSearchMetaDefinitions(): SearchMetaDefinitionsVue {

    if (!searchMetaDefinitions) {

        throw new Error("searchMetaDefinitions is undefined");
    }

    return searchMetaDefinitions;
}

/**
 * can check to see if the searchMetaDefinitions are set.
 * 
 * @returns
 */
export function hasSearchMetaDefinitions() {

    return !!searchMetaDefinitions;
}

/**
 * The initial search is done with server side rendering, this method
 * returns the data about that initial search, which normally come with a 
 * new fetch calls. This contains information such as the number of results,
 * page, etc.
 * 
 * Note that setting these properties in c# is done through the SearchSetupHelper class.
 * 
 * @returns
 */
export function getSearchBaseResultsFromServerSideRendering(createEmptyIfNotExists: boolean = false): SearchResultBaseVue {

    if (!initialSearchResults) {


        if (createEmptyIfNotExists) {
            return new SearchResultBaseVue(0, 0, 0, 0, false);
        }

        throw new Error("searchResultBase is undefined");
    }

    return initialSearchResults;
}


export function getBoundingBoxForLocationSelection(filter: FilterSelectionLocation) {

    if (filter.hasValue()) {

        const sm = getSearchMetaDefinitions();
        // try get for: sm.countriesWithRegions
        const bbs = new Array<GeoBoundingBoxTSVM>();
        for (let sel of filter.selection) {

            const bb = tryGetBoundingBox(sel);
            if (bb) {
                bbs.push(bb);
            }
        }

        // can also try with custom selections:
        //sm.locationSelections

        // future: landing page specific locations?

        // merge bbs to single bb (or null if no bb's found)
        return mergeBoundingBoxes(bbs);

    } else {
        // should reset to outer dimensions?
    }

    // fall back:


    return null;


}

/**
 * will merge bounding boxes into a single bounding box.
 *     For b1 (top-left):
        Get the minimum longitude (most left) from all b1 points.
        Get the maximum latitude (most top) from all b1 points.
    For b2 (bottom-right):
        Get the maximum longitude (most right) from all b2 points.
        Get the minimum latitude (most bottom) from all b2 points.
 * @param bbs
 * @returns
 */
export function mergeBoundingBoxes(bbs: Array<GeoBoundingBoxTSVM>) {

    if (bbs == null || bbs.length == 0) {
        return null;
    }
    let minLng = bbs[0].b1.lng; // Most left
    let maxLat = bbs[0].b1.lat; // Most top
    let maxLng = bbs[0].b2.lng; // Most right
    let minLat = bbs[0].b2.lat; // Most bottom

    for (let bb of bbs) {
        minLng = Math.min(minLng, bb.b1.lng);
        maxLat = Math.max(maxLat, bb.b1.lat);
        maxLng = Math.max(maxLng, bb.b2.lng);
        minLat = Math.min(minLat, bb.b2.lat);
    }

    return new GeoBoundingBoxTSVM(new GeoPointTSVM(maxLat, minLng), new GeoPointTSVM(minLat, maxLng));
}

/**
 * will search the searchmeta definitions for the relevant entries (lowest level possible, e.g. city first then up to country).
 * will also fall back to country if bb not set in other levels.
 * 
 * @param sel
 * @returns
 */
export function tryGetBoundingBox(sel: FilterSelectionLocationEntry): GeoBoundingBoxTSVM | null {

    let bb: GeoBoundingBoxTSVM | null = null;

    // note that data is nested, so we have to zoom in...

    if (sel.countryValueId) {

        const country = getSearchMetaDefinitions().countriesWithRegions.find(t => t.id == sel.countryValueId);


        if (country) {

            if (sel.regionValueId) {

                const region = country.subEntries.find(t => t.id == sel.regionValueId);
                if (region && region.geoBox) {

                    bb = region.geoBox;
                }



            }




            // fallback to country if needed.
            if (!bb && country.geoBox) {

                bb = country.geoBox;
            }
        }
    }



    return bb;
}



/**
 * Helper function that can be used to read the location details for the initial
 * server side rendering. we have no complete list of location details, as they
 * are fetched from server on typeahead. Therefore we cannot show the initial
 * names, and this data is set on the initial search request, inside the searchMetaDefinitions .
 * 
 * @param id
 */
export function getInitialLocationSelectionDetails(id: number) {

    const sm = getSearchMetaDefinitions();
    return sm.locationSelections.find(t => t.id == id);

}

/**
 * checks to see if in the initial search meta definitions there are any
 * locations selected. 
 * @returns
 */
export function hasInitialLocationSelectionOnInit() {

    if (hasSearchMetaDefinitions()) {

        const sm = getSearchMetaDefinitions();
        return sm.locationSelections?.length > 0;
    }

    return false;

}


// todo: perhaps we should have an alt version that 
// should not clear all filters, but only the landing page data specifi.c (like we had
// in a prev version)
export function clearFiltersAndRedirect() {

    // basically, this should clear everything, and reset full.
    // the easiest way it to in fact do a clean redirect to the current search (map/non map).
    // as that would automatically clear any and all filters, such as landing page, provider,
    // etc.

    let urlAction = getSearchMetaDefinitions().searchUrl;

    if (getSearchMetaDefinitions().isMapSearch) {

        urlAction = getSearchMetaDefinitions().mapSearchUrl;

    }

    let url = new URL(urlAction, window.location.href);
    window.location.assign(url.href);


    ////if (isLandingPage.value || isProviderPage.value) {
    ////    var searchQuery = filterToQuery(filter, searchModel.paging.clone(0));
    ////    const url = new URL(searchComposables.getSearchMetaDefinitions().searchUrl, window.location.href).href + '?' + searchQuery;

    ////    // console.debug("clearning landing page, redirecting to default search page...");
    ////    window.location.assign(url);
    ////}
}



